// src/components/ProfilePreview.js - Updated with link image support
import React, { useState, useEffect } from 'react';
import { doc, onSnapshot, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import availableIcons from './icons';
import BannerUpload from './BannerUpload';
import styles from './ProfilePreview.module.css';
import { getPaletteColors } from '../utils/templateUtils';
import { Phone, Mail, Ghost } from 'lucide-react';

const ProfilePreview = ({ userData, setUserData, getDarkestColor, isProUser, onUpgradeClick }) => {
  const [profile, setProfile] = useState(userData);
  const [hasShownGhostMode, setHasShownGhostMode] = useState(false);
  const [currentTheme, setCurrentTheme] = useState(userData?.profile?.theme || 'classic');
  
  // State for ghost password handling
  const [ghostLinkInputs, setGhostLinkInputs] = useState({});
  const [ghostLinkErrors, setGhostLinkErrors] = useState({});
  const [unlockedLinks, setUnlockedLinks] = useState({});
  const [showPasswordFields, setShowPasswordFields] = useState({});

  // Real-time updates from Firestore
  useEffect(() => {
    if (!userData?.uid) {
      console.error("ProfilePreview: Missing userData.uid");
      return;
    }
    
    // Set the initial theme
    setCurrentTheme(userData.profile?.theme || 'classic');
    
    const userDocRef = doc(db, 'users', userData.uid);
    const unsubscribe = onSnapshot(
      userDocRef,
      (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data();
          console.log('Firestore document update:', data);
          setProfile(data);
          
          // We no longer need to track ghost mode enabled state since we use a subtle indicator
          const ghostModeEnabledInData = data.profile?.ghostMode?.enabled === true;
          if (ghostModeEnabledInData && !hasShownGhostMode) {
            setHasShownGhostMode(true);
          }
          
          // Update theme when it changes
          if (data.profile?.theme) {
            setCurrentTheme(data.profile.theme);
          }
        } else {
          console.error('ProfilePreview: User document does not exist.');
        }
      },
      (error) => {
        console.error('ProfilePreview: Error reading user document:', error);
      }
    );
    return () => unsubscribe();
  }, [userData.uid, userData.profile?.theme, hasShownGhostMode]);

  // Keep local state updated with userData changes (particularly for text color and opacity)
  useEffect(() => {
    setProfile(userData);
  }, [userData, userData.profile?.textColor, userData.profile?.textOpacity]);

  // Direct fix for banner container height issue
  useEffect(() => {
    // Function to ensure banner container has correct height
    const fixBannerHeight = () => {
      // Find all banner containers
      const bannerContainers = document.querySelectorAll('.bannerThemeContainer');
      bannerContainers.forEach(container => {
        // Set height based on screen size - using specific preview sizes
        const previewHeight = window.innerWidth <= 768 ? '120px' : '140px';
        
        // Apply consistent styling
        container.style.height = previewHeight;
        container.style.maxHeight = previewHeight;
        container.style.minHeight = previewHeight;
        container.style.boxSizing = 'border-box';
        container.style.overflow = 'hidden';
        container.style.display = 'block';
      });
    };
    
    // Run the fix after a short delay to ensure DOM is fully rendered
    const timer = setTimeout(fixBannerHeight, 100);
    
    // Also run on resize
    window.addEventListener('resize', fixBannerHeight);
    
    // Cleanup
    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', fixBannerHeight);
    };
  }, []);

  const handleBannerUpload = async (newBannerData) => {
    if (!userData?.uid) {
      console.error("ProfilePreview: Cannot update banner, missing userData.uid");
      return;
    }
    console.log("Updating banner for user:", userData.uid, "with data:", newBannerData);
    const userDocRef = doc(db, 'users', userData.uid);
    try {
      if (!newBannerData.url) {
        // Remove banner
        await updateDoc(userDocRef, {
          'profile.banner': null,
        });
        setProfile((prev) => ({
          ...prev,
          profile: { ...prev.profile, banner: null },
        }));
        setUserData((prev) => ({
          ...prev,
          profile: { ...prev.profile, banner: null },
        }));
      } else {
        // Update banner with new data
        await updateDoc(userDocRef, {
          'profile.banner': {
            url: newBannerData.url,
            position: newBannerData.position,
          },
        });
        setProfile((prev) => ({
          ...prev,
          profile: {
            ...prev.profile,
            banner: {
              url: newBannerData.url,
              position: newBannerData.position,
            },
          },
        }));
        setUserData((prev) => ({
          ...prev,
          profile: {
            ...prev.profile,
            banner: {
              url: newBannerData.url,
              position: newBannerData.position,
            },
          },
        }));
      }
      console.log("Banner update successful.");
    } catch (error) {
      console.error('Error updating banner:', error);
    }
  };

  // Toggle password field visibility
  const togglePasswordField = (linkId) => {
    setShowPasswordFields(prev => ({
      ...prev,
      [linkId]: !prev[linkId]
    }));
  };

  // Handle password input change
  const handlePasswordInputChange = (linkId, value) => {
    setGhostLinkInputs(prev => ({
      ...prev,
      [linkId]: value
    }));
    
    // Clear error when typing
    if (ghostLinkErrors[linkId]) {
      setGhostLinkErrors(prev => ({
        ...prev,
        [linkId]: ''
      }));
    }
  };

  // Handle password submission for ghost links
  const handleGhostLinkSubmit = (linkId, e) => {
    if (e) {
      e.preventDefault();
    }
    
    const link = profile.profile?.links.find(l => l.id === linkId);
    const inputPassword = ghostLinkInputs[linkId] || '';
    
    if (!link || !link.ghostPassword) {
      setGhostLinkErrors(prev => ({
        ...prev,
        [linkId]: 'Invalid link'
      }));
      return;
    }
    
    if (inputPassword === link.ghostPassword) {
      setUnlockedLinks(prev => ({
        ...prev,
        [linkId]: true
      }));
      setGhostLinkErrors(prev => ({
        ...prev,
        [linkId]: ''
      }));
      setShowPasswordFields(prev => ({
        ...prev,
        [linkId]: false
      }));
    } else {
      setGhostLinkErrors(prev => ({
        ...prev,
        [linkId]: 'Incorrect password'
      }));
    }
  };

  // Function to get text color with opacity
  const getTextColorWithOpacity = () => {
    // Default to a dark color with 70% opacity if nothing is set
    const defaultColor = '#444444';
    const defaultOpacity = 70;
    
    // Use the textColor from profile if available
    const textColor = profile?.profile?.textColor || defaultColor;
    
    // Check if the color already has an opacity component (8 digits with alpha)
    if (textColor && textColor.length === 9) {
      // Color already has opacity in format #RRGGBBAA
      return textColor;
    }
    
    // Use the stored opacity or default
    const opacity = profile?.profile?.textOpacity || defaultOpacity;
    
    // Convert opacity to hex
    const alphaHex = Math.round((opacity / 100) * 255).toString(16).padStart(2, '0');
    
    // Return the color with opacity
    return `${textColor}${alphaHex}`;
  };

  if (!profile) {
    return <div>Loading profile...</div>;
  }

  // Get palette colors for styling
  const paletteColors = getPaletteColors(profile);
  
  // Assign palette colors to different elements
  const backgroundColor = paletteColors[4] || '#ffffff'; // Full page background
  const containerBackground = paletteColors[3] || '#f5f5f5'; // 60% container background
  const linkBackground = paletteColors[1] || '#f13535'; // Link background
  
  
  // Get text color with opacity
  const textColorWithOpacity = getTextColorWithOpacity();
  
  // Get the primary color for links from the palette
  const darkColor = getDarkestColor();

  // Filter links to show only non-hidden ones
  const unsortedLinks = profile.profile?.links?.filter((link) => !link.hidden) || [];
  
  // Sort links with pinned items first, then by order
  const visibleLinks = [...unsortedLinks].sort((a, b) => {
    if (a.pinned && !b.pinned) return -1;
    if (!a.pinned && b.pinned) return 1;
    return a.order - b.order;
  });

  // Password form styles
  const passwordFormStyle = {
    marginTop: '5px',
    marginBottom: '8px',
    marginLeft: 'auto',
    marginRight: 'auto',
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    padding: '2px 12px',
    borderRadius: '50px',
    boxShadow: '0 2px 5px rgba(0, 0, 0, 0.1)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '50%'
  };

  const passwordFormContentStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    width: '100%'
  };

  const passwordInputStyle = {
    flexGrow: 1,
    padding: '6px 10px',
    border: 'none',
    borderRadius: '30px',
    fontSize: '0.9rem',
    textAlign: 'center',
    letterSpacing: '1px',
    fontFamily: 'monospace',
    outline: 'none',
    background: 'transparent',
    width: '50%'
  };

  const passwordButtonStyle = {
    background: 'transparent',
    border: 'none',
    padding: '4px',
    cursor: 'pointer',
    color: '#555',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  const passwordErrorStyle = {
    fontSize: '0.8rem',
    color: '#ff4444',
    marginTop: '4px',
    textAlign: 'center',
    width: '100%'
  };

  // Check if a banner image exists
  const backgroundImageClass = profile.profile?.banner?.url ? 'has-background-image' : '';

  // This mini-component will render BannerUpload in a container that inherits theme styles
  const ThemedBanner = () => (
    <div 
      className={styles.bannerThemeContainer}
    >
      <BannerUpload
        currentBanner={profile.profile?.banner}
        onBannerUpload={handleBannerUpload}
        editable={true}
        templateName={currentTheme}
        colors={paletteColors}
      />
    </div>
  );

  return (
    <div 
      className={`${styles.profilePreviewContainer} theme-${currentTheme} profile-container`} 
      style={{ backgroundColor }}
    >
      {/* Center container with direct background image application */}
      <div 
        className={`${styles.centerContainer} hasBackgroundImage ${backgroundImageClass}`} 
        style={{ backgroundColor: containerBackground }}
      />
      
      {/* Subtle Ghost Mode Indicator */}
      {profile.profile?.ghostMode?.enabled && (
        <div className={styles.subtleGhostIndicator}>
          <Ghost size={18} strokeWidth={1.5} />
          <span>Ghost Mode Active - Profile password protected</span>
        </div>
      )}
      
      {/* Theme-compatible banner - outside the center container, full width */}
      <ThemedBanner />

      {/* Main content area - outside the center container, full width */}
      <div className={`${styles.previewContent} profile-content`} style={{ marginTop: '20px' }}>
        {/* Avatar container - inherits theme avatar styles */}
        <div className={`${styles.avatarContainer} avatar-container ${styles.previewAvatarContainer}`}>
          {profile.photoURL ? (
            <img src={profile.photoURL} alt="Profile" className={styles.avatarImage} />
          ) : (
            <div className="initials">
              {profile.username?.[0]?.toUpperCase() || profile.profile?.displayName?.[0]?.toUpperCase() || '?'}
            </div>
          )}
        </div>
        
        {/* User name - inherits theme heading styles and uses text color with opacity */}
        <h2 className={styles.previewHeading} style={{ color: textColorWithOpacity }}>
          {profile.profile?.displayName || profile.username}
        </h2>
        
        {/* Contact info - inherit theme text styles and text color with opacity */}
        <div className={styles.contactInfo}>
          {profile.profile?.email && (
            <div className={styles.contactItem} style={{ color: textColorWithOpacity }}>
              <Mail size={16} /> <span>{profile.profile.email}</span>
            </div>
          )}
          
          {profile.profile?.phone && (
            <div className={styles.contactItem} style={{ color: textColorWithOpacity }}>
              <Phone size={16} /> <span>{profile.profile.phone}</span>
            </div>
          )}
        </div>
        
        {/* Bio - inherits theme bio styles and text color with opacity */}
        {profile.profile?.bio && (
          <p className="bio" style={{ color: textColorWithOpacity }}>
            {profile.profile.bio}
          </p>
        )}

        {/* Social icons - inherits theme icon styles */}
        {profile.profile?.icons && profile.profile.icons.length > 0 && (
          <div className="social-icons">
            {profile.profile.icons.map((iconData, index) => {
              const iconObj = availableIcons.find((icon) => icon.name === iconData.component);
              if (!iconObj) return null;
              const IconComponent = iconObj.component;
              return (
                <a
                  key={index}
                  href={iconData.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="icon"
                  style={{ 
                    // Removed borderColor from inline styles to let CSS handle it
                    color: iconData.color || darkColor
                  }}
                >
                  <IconComponent
                    size={24}
                    stroke={iconData.color || darkColor}
                    color={iconData.color || darkColor}
                  />
                </a>
              );
            })}
          </div>
        )}

        {visibleLinks.length > 0 && (
          <div className="links-container">
            {visibleLinks.map((link, index) => {
              // For ghost-protected links that haven't been unlocked
              if (link.ghostProtected && !unlockedLinks[link.id]) {
                return (
                  <div key={index} className={styles.ghostProtectedLinkWrapper}>
                    <button
                      onClick={() => togglePasswordField(link.id)}
                      className={`link-button ${link.imageUrl ? 'has-image' : ''}`}
                      style={{
                        backgroundColor: linkBackground,
                        color: textColorWithOpacity
                      }}
                    >
                      {link.imageUrl && (
                        <div className="link-image-container">
                          <img 
                            src={link.imageUrl} 
                            alt="" 
                            className="link-thumbnail" 
                          />
                        </div>
                      )}
                      <span className="link-title">{link.title}</span>
                      <Ghost size={16} className={styles.linkGhostIcon} />
                    </button>
                    
                    {showPasswordFields[link.id] && (
                      <div style={passwordFormStyle}>
                        <form onSubmit={(e) => handleGhostLinkSubmit(link.id, e)} style={passwordFormContentStyle}>
                          <input
                            type="text"
                            style={passwordInputStyle}
                            placeholder="Enter password"
                            value={ghostLinkInputs[link.id] || ''}
                            onChange={(e) => handlePasswordInputChange(link.id, e.target.value)}
                            maxLength={6}
                            autoFocus
                          />
                          <button type="submit" style={passwordButtonStyle}>
                            <Ghost size={16} />
                          </button>
                        </form>
                        {ghostLinkErrors[link.id] && (
                          <div style={passwordErrorStyle}>
                            {ghostLinkErrors[link.id]}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              }
                      
              return (
                <a
                  key={index}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`link-button ${link.imageUrl ? 'has-image' : ''}`}
                  style={{
                    backgroundColor: linkBackground,
                    color: textColorWithOpacity
                  }}
                >
                  {link.imageUrl && (
                    <div className="link-image-container">
                      <img 
                        src={link.imageUrl} 
                        alt="" 
                        className="link-thumbnail" 
                      />
                    </div>
                  )}
                  <span className="link-title">{link.title}</span>
                  {link.ghostProtected && (
                    <Ghost size={16} className={styles.linkGhostIcon} />
                  )}
                </a>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProfilePreview;